import React, { CSSProperties, useMemo } from "react";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../../ui/tooltip";
import { format, isSameDay, parseISO } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import capitalize from "lodash/capitalize";
import sanitizeHtml from "sanitize-html";

import { CollaborationPostStatus, User } from "../../../../features/user";
import { userClockFormat, userPostStatusList } from "../../../../utils/user";
import { ColCollaborator, ColComment } from "../../types";
import { POST_STATES } from "../../../../constants/PostsView";
import { buildSpansWithMatchedRegex } from "../../../../ui/sked-editor/editor-state-conversion";

export const COMMENT_TYPES = {
  USER_WRITTEN: "USER_WRITTEN",
  SYSTEM_GENERATED: "SYSTEM_GENERATED",
  DELETED: "DELETED",
} as const;

// Collaboration comments intentionally render limited HTML returned by the
// server so formatting from SKE-2590 is preserved. Keep sanitizing before
// passing it to dangerouslySetInnerHTML to avoid XSS and accidental regressions.
function createSanitizedMarkup(html: string) {
  return { __html: sanitizeHtml(html) };
}

const commentContentWrapStyle: CSSProperties = {
  overflowWrap: "anywhere",
  wordBreak: "break-word",
};

interface UseCollaborationMessageParams {
  comment: ColComment;
  previousComment?: ColComment;
  nextComment?: ColComment;
  collaborators: ColCollaborator[];
  currentUser: User;
  onDelete?: (comment: ColComment) => void;
  onEdit?: (comment: ColComment) => void;
  isEditing?: boolean;
  postState: string;
  disabled?: boolean;
}

interface UseCollaborationMessageReturn {
  // Sender & Display
  sender: ColCollaborator;
  displayName: string;

  // Time & Format
  clockFormat: string;
  formattedTime: string;

  // Message State
  isOwnMessage: boolean;
  isExternal: boolean;
  isUserComment: boolean;
  isDeleted: boolean;
  isSystem: boolean;
  isAdmin: boolean;
  isUnread: boolean;
  isChangeRequest: boolean;

  // Display Logic
  showDateSeparator: boolean;
  dateSeparatorLabel?: string;
  canStackWithPrevious: boolean;
  canStackWithNext: boolean;
  showHeader: boolean;
  showAvatar: boolean;
  showTimeInGutter: boolean;
  gutterTime?: string;

  // Header & Badge
  headerLabel?: string;
  headerBadge?: React.ReactNode;

  // Permissions & Actions
  canEdit: boolean;
  canDelete: boolean;
  actions?: {
    onEdit?: () => void;
    onDelete?: () => void;
  };

  // Content
  content: React.ReactNode | null;
  postStatuses: CollaborationPostStatus[];
}

function resolveSender(collaborators: ColCollaborator[], comment: ColComment) {
  const sender = collaborators.find((col) => col._id === comment.createdBy);
  if (sender) {
    return sender;
  }
  const metadataName = comment.metadata?.username;
  if (metadataName) {
    const [firstName, ...rest] = metadataName.split(" ");
    return {
      firstName: firstName || metadataName,
      lastName: rest.join(" "),
      username: metadataName,
    } as ColCollaborator;
  }
  return {
    firstName: "",
    lastName: "",
    username: "unknown",
  } as ColCollaborator;
}

function getDisplayName(sender: ColCollaborator, comment: ColComment) {
  if (comment.authorType === "integration") {
    return (
      comment.authorName ||
      `${[sender?.firstName, sender?.lastName].filter(Boolean).join(" ") || "Unknown user"}'s Agent`
    );
  }
  if (comment.metadata?.username) {
    return comment.metadata.username;
  }
  const firstName = sender?.firstName || "";
  const lastName = sender?.lastName || "";
  const combined = `${firstName} ${lastName}`.trim();
  if (combined) {
    return combined;
  }
  if (sender?.username) {
    return sender.username;
  }
  return "Unknown user";
}

// Format a UTC instant in the account's profile timezone (matching the
// calendar/archive via getFormattedPostDate, SKE-2716). Falls back to
// browser-local formatting when no timezone is given or the IANA name is
// invalid so old accounts with an empty/garbage timezone never throw.
export function formatDateSafe(
  date: string,
  formatStr: string,
  timezone?: string
) {
  if (timezone) {
    try {
      return formatInTimeZone(date, timezone, formatStr);
    } catch {
      // Invalid IANA timezone — fall through to browser-local.
    }
  }
  return format(parseISO(date), formatStr);
}

function formatTime(date?: string, clockFormat?: string, timezone?: string) {
  if (!date) {
    return "";
  }
  return formatDateSafe(date, clockFormat || "HH:mm", timezone);
}

function isSameDayInTimeZone(a: string, b: string, timezone?: string) {
  if (timezone) {
    try {
      return (
        formatInTimeZone(a, timezone, "yyyy-MM-dd") ===
        formatInTimeZone(b, timezone, "yyyy-MM-dd")
      );
    } catch {
      // Invalid IANA timezone — fall through to browser-local comparison.
    }
  }
  return isSameDay(parseISO(a), parseISO(b));
}

function shouldShowDateSeparator(
  comment: ColComment,
  previousComment?: ColComment,
  timezone?: string
) {
  if (!comment.createdAt) {
    return false;
  }
  if (!previousComment?.createdAt) {
    return true;
  }
  return !isSameDayInTimeZone(
    comment.createdAt,
    previousComment.createdAt,
    timezone
  );
}

function shouldStackWithPrevious(
  comment: ColComment,
  previousComment?: ColComment,
  timezone?: string
) {
  if (!previousComment) {
    return false;
  }
  if (
    comment.type !== COMMENT_TYPES.USER_WRITTEN ||
    previousComment.type !== COMMENT_TYPES.USER_WRITTEN
  ) {
    return false;
  }
  if (comment.createdBy !== previousComment.createdBy) {
    return false;
  }
  if (
    (comment.authorType || "user") !== (previousComment.authorType || "user") ||
    comment.authorName !== previousComment.authorName
  ) {
    return false;
  }
  if (comment.visibility !== previousComment.visibility) {
    return false;
  }
  if (!comment.createdAt || !previousComment.createdAt) {
    return false;
  }
  return isSameDayInTimeZone(
    comment.createdAt,
    previousComment.createdAt,
    timezone
  );
}

export function VisibilityBadge({ label }: { label: string }) {
  return (
    <span className="rounded-[8px] bg-[#F59E0B] px-[8px] py-[0] text-[10px] font-[500] text-white leading-[14px]">
      {label}
    </span>
  );
}

export function UserMessageContentV2({
  comment,
  collaborators,
  compact = false,
}: {
  comment: ColComment;
  collaborators: ColCollaborator[];
  compact?: boolean;
}) {
  const content = buildSpansWithMatchedRegex(
    comment.body || "",
    /@\[([^\]]+)\]/g
  ).map((span, i) => {
    switch (span.type) {
      case "normal": {
        return (
          <span
            key={i}
            dangerouslySetInnerHTML={createSanitizedMarkup(span.text)}
          />
        );
      }
      case "regex": {
        const collaborator = collaborators.find(
          (collab) => collab._id === span.match[1]
        );
        const label = collaborator
          ? [collaborator.firstName, collaborator.lastName]
              .filter(Boolean)
              .join(" ") || "unknown"
          : "unknown";
        return (
          <span key={i} className="text-blue-500 font-semibold">
            {label}
          </span>
        );
      }
    }
  });

  return (
    <p
      className={`whitespace-pre-wrap break-words text-[#333] m-0 ${compact ? "text-[12px] leading-[16px]" : "text-[14px] leading-[20px]"}`}
      style={commentContentWrapStyle}
    >
      {content}
    </p>
  );
}

function AnnotationFeedbackHeading({ isAiSummary }: { isAiSummary?: boolean }) {
  const icon = (
    <span className="grid size-4 shrink-0 place-items-center text-xs text-neutral-500">
      <i className="fa-regular fa-sparkles" aria-hidden="true" />
    </span>
  );

  return (
    <div className="mb-1 flex items-center gap-1 text-xs font-semibold leading-4">
      Feedback
      {isAiSummary ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label="Summary of annotations is generated by AI."
              className="!min-w-0 !h-4 !w-4 !p-0 border-0 bg-transparent text-xs"
            >
              {icon}
            </button>
          </TooltipTrigger>
          <TooltipContent>
            Summary of annotations is generated by AI.
          </TooltipContent>
        </Tooltip>
      ) : (
        icon
      )}
    </div>
  );
}

function DeletedMessageContent({ isExternal }: { isExternal: boolean }) {
  return (
    <p className="whitespace-pre-wrap break-words text-[12px] italic text-gray-500 m-0 leading-[16px]">
      {isExternal
        ? "This message was deleted by admin."
        : "This message was deleted."}
    </p>
  );
}

export function SystemGeneratedComment({
  comment,
  postStatuses,
  clockFormat,
  timezone,
}: {
  comment: ColComment;
  sender: ColCollaborator;
  clockFormat: string;
  postStatuses: CollaborationPostStatus[];
  timezone?: string;
}) {
  const { metadata, body, event } = comment;
  const oldStatus = postStatuses.find(
    (status) => status.key === metadata?.oldStatus
  );
  const newStatus = postStatuses.find(
    (status) => status.key === metadata?.newStatus
  );
  const createdStatus = postStatuses.find(
    (status) => status.key === metadata?.statusId
  );

  const statusColor = newStatus?.color || createdStatus?.color || "#D1D5DB";

  if (event === "POST_TYPE_CHANGE") {
    const newPostType = metadata?.newPostType
      ? capitalize(metadata.newPostType)
      : undefined;
    const oldPostType = metadata?.oldPostType
      ? capitalize(metadata.oldPostType)
      : undefined;
    const scheduleTimeFormat = `EEE dd MMM ${clockFormat || "HH:mm"}`.trim();
    const scheduledFor = metadata?.scheduleTime
      ? formatDateSafe(
          metadata.scheduleTime,
          scheduleTimeFormat,
          timezone || "UTC"
        )
      : undefined;

    if (newPostType === "Schedule" && scheduledFor) {
      return (
        <div className="rounded-[8px] border border-neutral-100 bg-white p-[8px] text-[12px] leading-[16px] font-normal text-[#454545] m-0">
          <div className="flex items-start gap-3">
            <span className="inline-flex h-[18px] w-[18px] items-center justify-center text-[#6A5BFF]">
              <i className="fa-regular fa-alarm-clock text-[14px]" />
            </span>
            <div className="flex flex-col gap-[4px]">
              <span className="font-[400] text-[12px]">Scheduled for:</span>
              <span className="text-[12px] font-[700] text-[#1F1F1F]">
                {scheduledFor}
              </span>
            </div>
          </div>
        </div>
      );
    }

    const parts: string[] = [];
    if (oldPostType) {
      parts.push(oldPostType);
    }
    if (newPostType) {
      parts.push(newPostType);
    }
    const changeLabel =
      parts.length === 2
        ? `${parts[0]} → ${parts[1]}`
        : newPostType
          ? `${newPostType}`
          : "a new type";

    return (
      <div className="rounded-[8px] border border-neutral-100 bg-white p-[8px] text-[12px] text-[#454545] m-0 leading-[16px]">
        <div className="flex items-start gap-3">
          <span className="inline-flex h-[18px] w-[18px] items-center justify-center text-[#6A5BFF]">
            <i className="fa-regular fa-layer-group text-[14px]" />
          </span>
          <div className="flex flex-col gap-[4px]">
            <span className="font-[400] text-[12px]">Post type updated</span>
            <span className="text-[12px] font-[700] text-[#1F1F1F]">
              {changeLabel}
            </span>
          </div>
        </div>
      </div>
    );
  }

  if (newStatus) {
    return (
      <div className="rounded-[8px] border border-neutral-100 bg-white p-[8px] text-[12px] m-0">
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <p className="text-[12px] text-[#454545] m-0 leading-[16px]">
              <div className="flex items-center gap-2 leading-[16px]">
                <span className="w-[16px] h-[16px] flex items-center justify-center">
                  <span
                    className="inline-flex h-[8px] w-[8px] rounded-full"
                    style={{ backgroundColor: statusColor }}
                  />
                </span>
                Changed status:
              </div>

              {oldStatus && (
                <span className="pl-[22px]">
                  <span className="font-semibold">{oldStatus.label}</span>
                  <span className="mx-1 text-gray-500">→</span>
                </span>
              )}
              <span className="font-semibold">{newStatus.label}</span>
            </p>
            {body && (
              <div className="pl-[22px]">
                <div className="rounded-[8px] bg-[#FFF8F8] p-[8px] mt-[4px]">
                  {metadata?.markup ? (
                    <AnnotationFeedbackHeading
                      isAiSummary={metadata.isAiSummary}
                    />
                  ) : (
                    !metadata?.isAiSummary && (
                      <p className="text-[12px] font-semibold uppercase text-[#333] m-0">
                        Comment:
                      </p>
                    )
                  )}
                  <p
                    className="whitespace-pre-wrap font-[400] text-[12px] text-[#333] leading-[20px] m-0"
                    style={commentContentWrapStyle}
                    dangerouslySetInnerHTML={createSanitizedMarkup(body)}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (event === "POST_CREATED" && createdStatus) {
    const postType = metadata?.postType
      ? capitalize(metadata.postType)
      : "Post";
    return (
      <div className="rounded-[8px] border border-gray-200 bg-white p-[8px] text-sm leading-6 text-gray-900">
        <div className="flex items-center gap-3">
          <span
            className="inline-flex h-2.5 w-2.5 rounded-full"
            style={{ backgroundColor: statusColor }}
          />
          <p className="text-[12px] text-[#454545] m-0 leading-[16px]">
            Created {postType} as{" "}
            <span className="font-semibold">{createdStatus.label}</span>
          </p>
        </div>
      </div>
    );
  }

  return null;
}

export function getMessageContent({
  comment,
  collaborators,
  sender,
  postStatuses,
  clockFormat,
  timezone,
}: {
  comment: ColComment;
  collaborators: ColCollaborator[];
  sender: ColCollaborator;
  postStatuses: CollaborationPostStatus[];
  clockFormat: string;
  timezone?: string;
}) {
  if (comment.type === COMMENT_TYPES.DELETED) {
    return (
      <DeletedMessageContent isExternal={comment.visibility === "EXTERNAL"} />
    );
  }

  if (comment.type === COMMENT_TYPES.USER_WRITTEN) {
    if (comment.metadata?.markup)
      return (
        <div className="rounded-lg bg-blue-25 p-2 text-neutral-800">
          <AnnotationFeedbackHeading
            isAiSummary={comment.metadata.isAiSummary}
          />
          <UserMessageContentV2
            comment={comment}
            collaborators={collaborators}
            compact
          />
        </div>
      );
    return (
      <UserMessageContentV2 comment={comment} collaborators={collaborators} />
    );
  }

  if (comment.type === COMMENT_TYPES.SYSTEM_GENERATED) {
    return (
      <SystemGeneratedComment
        comment={comment}
        sender={sender}
        clockFormat={clockFormat}
        postStatuses={postStatuses}
        timezone={timezone}
      />
    );
  }

  return null;
}

export function useCollaborationMessage({
  comment,
  previousComment,
  nextComment,
  collaborators,
  currentUser,
  onDelete,
  onEdit,
  postState,
  disabled,
}: UseCollaborationMessageParams): UseCollaborationMessageReturn {
  // Sender & Display
  const sender = useMemo(
    () => resolveSender(collaborators, comment),
    [collaborators, comment]
  );

  const displayName = useMemo(
    () => getDisplayName(sender, comment),
    [sender, comment]
  );

  // Time & Format
  // Render all comment times in the account's profile timezone so the
  // collaboration paper trail matches the calendar/archive (SKE-2716).
  const timezone = currentUser.timezone;

  const clockFormat = useMemo(
    () => userClockFormat(currentUser) || "HH:mm",
    [currentUser]
  );

  const formattedTime = useMemo(
    () => formatTime(comment.createdAt, clockFormat, timezone),
    [comment.createdAt, clockFormat, timezone]
  );

  // Message State
  const isOwnMessage = useMemo(
    () =>
      Boolean(comment.createdBy && currentUser.userId === comment.createdBy),
    [comment.createdBy, currentUser.userId]
  );

  const isExternal = comment.visibility === "EXTERNAL";
  const isAgent = comment.authorType === "integration";

  const isUserComment = comment.type === COMMENT_TYPES.USER_WRITTEN;

  const isDeleted = comment.type === COMMENT_TYPES.DELETED;

  const isSystem = comment.type === COMMENT_TYPES.SYSTEM_GENERATED;

  const isAdmin = currentUser.role === "admin";

  const isUnread = useMemo(
    () =>
      isUserComment &&
      (!isOwnMessage || isAgent) &&
      !comment.readBy.includes(currentUser.userId),
    [isUserComment, isOwnMessage, isAgent, comment.readBy, currentUser.userId]
  );

  // Display Logic
  const showDateSeparator = useMemo(
    () => shouldShowDateSeparator(comment, previousComment, timezone),
    [comment, previousComment, timezone]
  );

  const dateSeparatorLabel = useMemo(
    () =>
      comment.createdAt
        ? formatDateSafe(comment.createdAt, "d MMM yyyy", timezone)
        : undefined,
    [comment.createdAt, timezone]
  );

  const canStackWithPrevious = useMemo(
    () => shouldStackWithPrevious(comment, previousComment, timezone),
    [comment, previousComment, timezone]
  );

  const canStackWithNext = useMemo(
    () =>
      nextComment
        ? shouldStackWithPrevious(nextComment, comment, timezone)
        : false,
    [nextComment, comment, timezone]
  );

  const showHeader = !canStackWithPrevious;

  const showAvatar = showHeader;

  const showTimeInGutter = useMemo(
    () => !showHeader && isUserComment,
    [showHeader, isUserComment]
  );

  const gutterTime = useMemo(
    () =>
      showTimeInGutter
        ? formatTime(comment.createdAt, "HH:mm", timezone)
        : undefined,
    [showTimeInGutter, comment.createdAt, timezone]
  );

  // Header & Badge
  const headerLabel = useMemo(
    () =>
      isAgent
        ? undefined
        : comment.metadata?.isAiSummary && !comment.metadata?.markup
          ? "AI Summary"
          : isOwnMessage
            ? "Yourself"
            : isSystem && isExternal
              ? "External reviewer"
              : undefined,
    [
      isAgent,
      comment.metadata?.isAiSummary,
      comment.metadata?.markup,
      isOwnMessage,
      isSystem,
      isExternal,
    ]
  );

  const headerBadge = useMemo(
    () =>
      isExternal && (isUserComment || isDeleted) ? (
        <VisibilityBadge label="External note" />
      ) : undefined,
    [isExternal, isUserComment, isDeleted]
  );

  // Post Statuses
  const postStatuses = useMemo(
    () => userPostStatusList(currentUser),
    [currentUser]
  );

  const isChangeRequest = useMemo(() => {
    if (
      !comment.event ||
      comment.event !== "POST_STATUS_CHANGE" ||
      !comment.metadata?.newStatus
    ) {
      return false;
    }

    const newStatus = postStatuses.find(
      (status) => status.key === comment.metadata?.newStatus
    );

    if (!newStatus) {
      return false;
    }

    // Check if the status label indicates a change request/rejection
    const changeRequestPatterns =
      /change.*request|request.*change|reject|denied|decline/i;
    return changeRequestPatterns.test(newStatus.label);
  }, [comment.event, comment.metadata?.newStatus, postStatuses]);

  // Permissions & Actions
  const isPostArchived = useMemo(
    () => postState === POST_STATES.SCHEDULED && disabled,
    [postState, disabled]
  );

  const canEdit = useMemo(
    () => Boolean(isUserComment && isOwnMessage && onEdit && !isPostArchived),
    [isUserComment, isOwnMessage, onEdit, isPostArchived]
  );

  const canDeleteOwn = useMemo(
    () => Boolean(isUserComment && isOwnMessage && onDelete && !isPostArchived),
    [isUserComment, isOwnMessage, onDelete, isPostArchived]
  );

  const canDeleteAsAdmin = useMemo(
    () =>
      Boolean(
        isUserComment &&
        isExternal &&
        !isOwnMessage &&
        isAdmin &&
        onDelete &&
        !isPostArchived
      ),
    [isUserComment, isExternal, isOwnMessage, isAdmin, onDelete, isPostArchived]
  );

  const canDelete = useMemo(
    () => canDeleteOwn || canDeleteAsAdmin,
    [canDeleteOwn, canDeleteAsAdmin]
  );

  const actions = useMemo(
    () =>
      canEdit || canDelete
        ? {
            onEdit: canEdit ? () => onEdit?.(comment) : undefined,
            onDelete: canDelete ? () => onDelete?.(comment) : undefined,
          }
        : undefined,
    [canEdit, canDelete, onEdit, onDelete, comment]
  );

  // Content
  const content = useMemo(
    () =>
      getMessageContent({
        comment,
        collaborators,
        sender,
        postStatuses,
        clockFormat,
        timezone,
      }),
    [comment, collaborators, sender, postStatuses, clockFormat, timezone]
  );

  return {
    // Sender & Display
    sender,
    displayName,

    // Time & Format
    clockFormat,
    formattedTime,

    // Message State
    isOwnMessage,
    isExternal,
    isUserComment,
    isDeleted,
    isSystem,
    isAdmin,
    isUnread,
    isChangeRequest,

    // Display Logic
    showDateSeparator,
    dateSeparatorLabel,
    canStackWithPrevious,
    canStackWithNext,
    showHeader,
    showAvatar,
    showTimeInGutter,
    gutterTime,

    // Header & Badge
    headerLabel,
    headerBadge,

    // Permissions & Actions
    canEdit,
    canDelete,
    actions,

    // Content
    content,
    postStatuses,
  };
}
